import * as React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  IconButton,
  Chip,
  Button,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { Link } from 'react-router-dom'
import { useTranslate } from 'ra-core'

const useStyles = makeStyles(theme => ({
  root: { maxWidth: 720, margin: '0 auto' },
  progressHeader: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 16,
    background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)',
    border: '1px solid #99f6e4',
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    color: '#0f766e',
  },
  card: {
    minHeight: 280,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  },
  cardActive: {
    transform: 'scale(1)',
    boxShadow: '0 12px 40px rgba(13,148,136,0.15)',
  },
  stepBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  responseList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(3),
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}))

const getQuestionLabel = (question, locale) => {
  if (!question || !question.label) return '—'
  return (
    question.label[locale] ||
    question.label.es ||
    Object.values(question.label)[0] ||
    '—'
  )
}

const CampaignQuestionFlow = ({ questionIds, questionList, locale }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const [currentStep, setCurrentStep] = React.useState(0)

  const orderedQuestions = React.useMemo(() => {
    if (!questionIds || questionIds.length === 0) return []
    const byId = {}
    ;(questionList || []).forEach(q => {
      byId[q.id] = q
    })
    return questionIds.map(id => byId[id]).filter(Boolean)
  }, [questionIds, questionList])

  React.useEffect(() => {
    setCurrentStep(0)
  }, [questionIds])

  if (orderedQuestions.length === 0) {
    return (
      <Box className={classes.empty}>
        <Typography variant="h6" gutterBottom>
          {translate('surveys.campaigns.no_questions_title')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {translate('surveys.campaigns.no_questions_hint')}
        </Typography>
      </Box>
    )
  }

  const total = orderedQuestions.length
  const current = orderedQuestions[currentStep]
  const progress = Math.round(((currentStep + 1) / total) * 100)

  return (
    <Box className={classes.root}>
      <Box className={classes.progressHeader}>
        <Box className={classes.progressLabel}>
          <Typography variant="subtitle1">
            {translate('surveys.campaigns.step_of', {
              current: currentStep + 1,
              total,
            })}
          </Typography>
          <Typography variant="subtitle1">{progress}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} />
      </Box>

      <Card className={`${classes.card} ${classes.cardActive}`}>
        <CardContent style={{ flex: 1, padding: 28 }}>
          <Box className={classes.stepBadge}>{currentStep + 1}</Box>
          <Typography variant="overline" color="textSecondary">
            {translate('surveys.resources.question')} #{current.id}
          </Typography>
          <Typography variant="h5" style={{ marginTop: 8, marginBottom: 16 }}>
            {getQuestionLabel(current, locale)}
          </Typography>
          <Chip
            size="small"
            label={current.responseType}
            style={{
              backgroundColor: '#e0e7ff',
              color: '#4338ca',
              fontWeight: 600,
            }}
          />
          {current.responseList && current.responseList.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{ marginTop: 24, marginBottom: 8 }}
              >
                {translate('surveys.resources.responses')} (
                {current.responseList.length})
              </Typography>
              <Box className={classes.responseList}>
                {current.responseList.map(response => (
                  <Chip
                    key={response.id}
                    label={getQuestionLabel(response, locale)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </>
          )}
          <Box style={{ marginTop: 24 }}>
            <Button
              component={Link}
              to={`/questions/${current.id}/show`}
              color="primary"
              variant="outlined"
              size="small"
            >
              {translate('surveys.actions.show_question')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box className={classes.nav}>
        <IconButton
          onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
          disabled={currentStep === 0}
          color="primary"
        >
          <ChevronLeftIcon />
        </IconButton>
        <Box display="flex" style={{ gap: 6 }}>
          {orderedQuestions.map((_, idx) => (
            <Box
              key={idx}
              onClick={() => setCurrentStep(idx)}
              style={{
                width: idx === currentStep ? 24 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: idx === currentStep ? '#0d9488' : '#cbd5e1',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Box>
        <IconButton
          onClick={() => setCurrentStep(s => Math.min(total - 1, s + 1))}
          disabled={currentStep === total - 1}
          color="primary"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  )
}

CampaignQuestionFlow.propTypes = {
  questionIds: PropTypes.array,
  questionList: PropTypes.array,
  locale: PropTypes.string.isRequired,
}

export default CampaignQuestionFlow
